/* eslint-disable no-console */
/* eslint-disable complexity */
import React, { useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import {
  FormCheckbox,
  FormDatePicker,
  FormTextarea
} from "protocol-setup/components/FormComponents";
import ButtonGroupSignoff from "protocol-setup/components/FormComponents/ButtonGroupSignoff";
import { yupResolver } from "@hookform/resolvers/yup";
import { dispatchRefreshEvent, safeTrim, useSetupPermission } from "protocol-setup/helpers";
import { useRequestHistorySubmit } from "protocol-setup/helpers/withRequestHistory";
import ProtectionElements from 'protocol-setup/helpers/ProtectionElement';
import { tabNames } from "../../orHelpers";

import {
  getActiveTab,
  getSaveButtonLabel,
  onSendReminder,
  onSubmit as onSubmitHelper,
  useFormEditability
} from "./pendingSignOffUtils";
import { createDynamicSchema } from "./validationSchemas";


const isValueUpdated = (newValue, defaultValue) => {
  if (defaultValue) {
    return newValue !== defaultValue;
  }
  return false;
};



const RenderSignOffInfo = ({ log, name, date, data = {} }) => {
  if (!log && (!name || !date)) return null;
  return (
    log ? log.map(x => {
      const comment = safeTrim(x.groupAdminComments);
      return comment && comment.length > 0 && (
        <div className="font-italic mt-n3 pt-1 mb-3 color-black">
          {comment} ({safeTrim(x.groupAdminSubmittedBy)}) {x.groupAdminSubmittedDate?.replace(/\s[A-Z]{3,4}$/, '')}
        </div>
      );
    }) : <div className="font-italic mt-n3 pt-1 mb-3 color-black">
      ({safeTrim(name)}) {date?.replace(/\s[A-Z]{3,4}$/, '')}
    </div>
  );
};

const PendingSignOffForm = ({
  protocolSetupData,
  fetchPsData,
  readOnly,
  onSuccessCallback,
  isEditMode,
  onToggleEditMode
}) => {
  const isIrocCredentialingInfoOpen =
    protocolSetupData?.isIrocCredentialingInfoOpen || false;

  const pendingSignOffProtectionElements = [
    ProtectionElements.OPEN_SIGNOFF_PENDINGSIGNOFF_GROUP,
    ProtectionElements.OPEN_SIGNOFF_PENDINGSIGNOFF_IROC,
    ProtectionElements.OPEN_SIGNOFF_PENDINGSIGNOFF_CTSU
  ];

  // Centralized editability logic
  const editability = useFormEditability(
    readOnly,
    protocolSetupData?.signoffPending || {},
    protocolSetupData?.requestStatus
  );
  const isOpenAdmin = useSetupPermission(ProtectionElements.OPEN_SIGNOFF_PENDINGSIGNOFF_CTSU);

  const [saveButtonLabel, setSaveButtonLabel] = useState(
    getSaveButtonLabel(protocolSetupData)
  );
  const [activeTab, setActiveTab] = useState(null);

  const resolver = useMemo(
    () => yupResolver(createDynamicSchema(activeTab)),
    [activeTab]
  );

  const defaultValues = {
    releaseDateTime: "",
    groupAdminSignOff: "",
    groupAdminComments: "",
    openAdminSignOff: "",
    openAdminComments: "",
    irocAdminSignOff: "",
    irocAdminComments: "",
    completed: false
  };

  const {
    control,
    handleSubmit,
    reset,
    trigger,
    getValues,
    setError,
    setValue,
    watch,
    formState: { isSubmitting, dirtyFields }
  } = useForm({
    resolver,
    mode: "onChange",
    defaultValues
  });

  const onReset = () => {
    reset();
    setSaveButtonLabel(getSaveButtonLabel(protocolSetupData, false, readOnly));
  };

  // Wrap onSubmitHelper with request history tracking
  const onSubmitWithHistory = useRequestHistorySubmit(
    async (data) => {
      return onSubmitHelper({
        dirtyFields,
        getValues,
        protocolSetupData,
        onSuccessCallback,
        dispatchRefreshEvent,
        fetchPsData,
        setError
      });
    },
    dirtyFields,
    tabNames.SIGNOFF_PENDING_SIGNOFF,
    "Signoff"
  );

  const memoizedProtocolSetupData = useMemo(
    () => protocolSetupData,
    [protocolSetupData]
  );

  const onSubmit = async () => {
    const data = getValues();
    const d = {
      ...data,
      requestNumber: memoizedProtocolSetupData?.requestNumber
    };
    await onSubmitWithHistory(d);
  };



  useEffect(() => {
    if (!isEditMode) {
      onReset();
    }
  }, [isEditMode]);

  useEffect(() => {
    if (memoizedProtocolSetupData) {
      const memoizedsignoffPendingData =
        memoizedProtocolSetupData?.signoffPending || {};
      const newValues = {
        releaseDateTime: memoizedsignoffPendingData?.releaseDt || null,
        groupAdminSignOff:
          memoizedsignoffPendingData?.groupAdminSignoff || false,
        // groupAdminComments: memoizedsignoffPendingData?.groupAdminComments || "",  removed as a change for PROT-795
        openAdminSignOff: memoizedsignoffPendingData?.openAdminSignoff || false,
        openAdminComments: memoizedsignoffPendingData?.openAdminComments || "",
        irocAdminSignOff: memoizedsignoffPendingData?.irocAdminSignoff || false,
        irocAdminComments: memoizedsignoffPendingData?.irocAdminComments || "",
        completed: false,
        groupAdminCompleted: memoizedsignoffPendingData?.groupAdminSignoff || false // added as a change for PROT-795
      };
      reset(newValues);
      const label = getSaveButtonLabel(
        memoizedProtocolSetupData,
        false,
        readOnly
      );
      setSaveButtonLabel(label);
      setValue("completed", label === "Submit");
    }
  }, [memoizedProtocolSetupData, reset, readOnly, setValue]);

  const timeOutTrigger = (name) => {
    setTimeout(() => {
      trigger(name);
    }, 0);
  }

  useEffect(() => {
    const subscription = watch((value, { name, type }) => {
      if (name) {
        setActiveTab(getActiveTab(name));
      }
    });
    return () => subscription.unsubscribe();
  }, [watch]);

  return (
    <div
      className={`form-layout-wrapper bg-transparent ${editability.shouldShowReadOnly && !isEditMode ? "read-only" : ""
        }`}
    >
      <form
        id="PendingSignOffForm"
        className="bg-transparent"
        onSubmit={handleSubmit(onSubmit)}
      >
        <div className="form-layout-body px-0">
          <span className="d-none"></span>
          <ul>
            <li className="row">
              {/* Group Admin Section */}
              <div className="col-4 mr-4 mb-0 form-sub-group">
                <div className="content-group-title">
                  Group Admin
                </div>
                <FormDatePicker
                  name="releaseDateTime"
                  section="openRave"
                  form="pendingSignOffForm"
                  control={control}
                  onChangeCallback={(newDate) => {
                    const isUpdated = isValueUpdated(
                      newDate,
                      memoizedProtocolSetupData?.signoffPending?.releaseDt
                    );
                    setSaveButtonLabel(
                      getSaveButtonLabel(protocolSetupData, isUpdated, readOnly)
                    );
                    if (isUpdated) {
                      setValue("completed", false);
                      setValue("groupAdminCompleted", false);
                    }
                    timeOutTrigger('releaseDateTime');
                  }}
                  pickDateTime
                  required
                  // Disable if user doesn't have permission and not in edit mode
                  disabled={!editability.fields.releaseDateTime && !isEditMode}
                  wrapperClassName="mb-3"
                />
                <FormCheckbox
                  className="pl-0 my-3 d-block"
                  labelClassName="color-dark-blue"
                  name="groupAdminSignOff"
                  section="openRave"
                  form="pendingSignOffForm"
                  control={control}
                  disabled={!editability.fields.groupAdminSignOff}
                  required
                  readOnly={readOnly}
                  wrapperClassName={`mb-3 ${readOnly || isEditMode ? 'read-only-show' : ''}`}
                  trigger={() => timeOutTrigger("groupAdminSignOff")}
                />
                <RenderSignOffInfo
                  name={protocolSetupData?.signoffPending?.openRaveSignoffLog?.[0]?.groupAdminSubmittedBy}
                  date={protocolSetupData?.signoffPending?.openRaveSignoffLog?.[0]?.groupAdminSubmittedDate}
                />
                <FormTextarea
                  name="groupAdminComments"
                  section="openRave"
                  form="pendingSignOffForm"
                  control={control}
                  disabled={!editability.fields.groupAdminComments && watch("groupAdminCompleted")}
                  trigger={() => timeOutTrigger("groupAdminComments")}
                  wrapperClassName="mb-3"
                />
                <RenderSignOffInfo log={protocolSetupData?.signoffPending?.openRaveSignoffLog} />
              </div>

              {/* OPEN Admin Section */}
              <div className="col-4 mr-4 mb-0 form-sub-group">
                <div className="content-group-title">OPEN Admin</div>
                <FormCheckbox
                  className="pl-0 mb-3 d-block"
                  labelClassName="color-dark-blue"
                  name="openAdminSignOff"
                  section="openRave"
                  form="pendingSignOffForm"
                  control={control}
                  required
                  disabled={!editability.fields.openAdminSignOff}
                  readOnly={readOnly}
                  wrapperClassName={readOnly ? 'read-only-show' : ''}
                  trigger={() => timeOutTrigger("openAdminSignOff")}
                />
                <RenderSignOffInfo
                  name={protocolSetupData?.signoffPending?.openAdminSubmittedBy}
                  date={protocolSetupData?.signoffPending?.openAdminSubmittedDate}
                />
                <FormTextarea
                  name="openAdminComments"
                  section="openRave"
                  form="pendingSignOffForm"
                  control={control}
                  disabled={!editability.fields.openAdminComments}
                  trigger={() => timeOutTrigger("openAdminComments")}
                />
              </div>

              {/* IROC Admin Section */}
              {isIrocCredentialingInfoOpen && (
                <div className="col-4 mr-4 mb-0 form-sub-group">
                  <div className="content-group-title">IROC Admin</div>
                  <FormCheckbox
                    className="pl-0 mb-3 d-block"
                    labelClassName="color-dark-blue"
                    name="irocAdminSignOff"
                    section="openRave"
                    form="pendingSignOffForm"
                    control={control}
                    required
                    disabled={!editability.fields.irocAdminSignOff}
                    readOnly={readOnly}
                    wrapperClassName={readOnly ? 'read-only-show' : ''}
                    trigger={() => timeOutTrigger("irocAdminSignOff")}
                  />
                  <RenderSignOffInfo
                    name={protocolSetupData?.signoffPending?.irocAdminSubmittedBy}
                    date={protocolSetupData?.signoffPending?.irocAdminSubmittedDate}
                  />
                  <FormTextarea
                    name="irocAdminComments"
                    section="openRave"
                    form="pendingSignOffForm"
                    control={control}
                    disabled={!editability.fields.irocAdminComments}
                    trigger={() => timeOutTrigger("irocAdminComments")}
                  />
                </div>
              )}
            </li>
          </ul>
        </div>

        <div className="form-layout-footer px-0 pb-0" data-testid={isEditMode ? "edit-mode" : "view-mode"} data-editable={editability.shouldShowReadOnly ? "no" : "yes"}>
          {(!editability.shouldShowReadOnly || isEditMode) && (
            <ButtonGroupSignoff
              onSubmit={handleSubmit(onSubmit)}
              onReset={onReset}
              isSubmitting={isSubmitting}
              saveLabel={saveButtonLabel}
              control={control}
              onSendReminder={
                !readOnly && !isEditMode
                  ? () => onSendReminder(memoizedProtocolSetupData?.requestNumber, isOpenAdmin)
                  : undefined
              }
              protectionElement={pendingSignOffProtectionElements}
              protectionElementSR={pendingSignOffProtectionElements}
            />
          )}

        </div>
      </form>
    </div>
  );
};

export default PendingSignOffForm;


